import { useState } from 'react'
import { Download, Eye, EyeOff, UserPlus } from 'lucide-react'
import Button from '../../components/Button'
import { FormBody, FormCols, InfoNote } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Drawer from '../../components/Drawer'
import TextField from '../../components/TextField'
import { getUserInfoFromDataSource } from './usersService'
import { strings as S } from './strings'
import './modals.css'

/** FormValidation rule from js.cshtml:1204-1266, kept verbatim. */
const PASSWORD_RULE = /^(?=.*[0-9])(?=.*[A-Za-z])(?=.*\W)(?!.* ).{6,15}$/
const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/* `arabicName`, `englishName` and `groupId` have no field on this form and are not typed:
   they arrive from the directory, and `CreateNewUser` takes all three. Without them a
   fetched account is created blank and has to be edited straight afterwards. */
const EMPTY = {
  username: '',
  password: '',
  confirmPassword: '',
  email: '',
  arabicName: '',
  englishName: '',
  groupId: null,
}

/**
 * `#add_modal` in single-user mode — `showCreationForm(false)`.
 *
 * The legacy form also posts a hidden `workingPeriod` field whose row carries
 * `style="display:none"`, so it is not rendered here either.
 */
export default function UserCreateModal({ open, onClose, onSubmit, sources = [], groups = [] }) {
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [reveal, setReveal] = useState({ password: false, confirmPassword: false })
  const [fetching, setFetching] = useState(false)
  const [fetched, setFetched] = useState(null)

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  /**
   * Fill the form from the configured directory.
   *
   * Offered only when the payload says a fetch source exists. The reply names the
   * department rather than identifying it, so the group is matched by LABEL against the
   * page's own list — no match simply leaves the department unset, which the edit form can
   * fix, rather than sending a name where the handler parses an id.
   */
  const fetchFromSource = async () => {
    const username = values.username.trim()
    if (!username) {
      setErrors((previous) => ({ ...previous, username: S.fetch.usernameRequired }))
      return
    }

    setFetching(true)
    const outcome = await getUserInfoFromDataSource(username, sources[0]?.id)
    setFetching(false)

    if (!outcome.ok || !outcome.profile) {
      setErrors((previous) => ({
        ...previous,
        username: outcome.message || S.fetch.notFound,
      }))
      setFetched(null)
      return
    }

    const profile = outcome.profile
    const group = groups.find(
      (option) => option.label === profile.groupNameAr || option.label === profile.groupNameEn,
    )

    setValues((previous) => ({
      ...previous,
      username: profile.userName || previous.username,
      email: profile.email || previous.email,
      arabicName: profile.nameAr,
      englishName: profile.nameEn,
      groupId: group?.value ?? null,
    }))
    setErrors((previous) => ({ ...previous, username: undefined, email: undefined }))
    setFetched({ ...profile, groupLabel: group?.label ?? profile.groupNameAr })
  }

  const validate = () => {
    const next = {}
    if (!values.username.trim()) next.username = S.create.errors.usernameRequired
    if (!values.password) next.password = S.create.errors.passwordRequired
    else if (!PASSWORD_RULE.test(values.password)) next.password = S.create.errors.passwordFormat
    if (!values.confirmPassword) next.confirmPassword = S.create.errors.confirmRequired
    else if (values.confirmPassword !== values.password)
      next.confirmPassword = S.create.errors.confirmMismatch
    if (!values.email.trim()) next.email = S.create.errors.emailRequired
    else if (!EMAIL_RULE.test(values.email)) next.email = S.create.errors.emailFormat
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!validate()) return
    // `confirmPassword` is not part of the legacy request body.
    const { confirmPassword: _confirmPassword, ...body } = values

    /* CLEAR ONLY ON SUCCESS. This used to reset the form on the line after `onSubmit`,
       synchronously — so a refusal the operator could act on ("that username is taken",
       "that password misses the policy") arrived over an empty form, and everything they
       had typed, both passwords included, had to be entered again. `onSubmit` resolves to
       whether the write succeeded. */
    const saved = await onSubmit(body)
    if (saved === false) return

    setValues(EMPTY)
    setErrors({})
    setFetched(null)
  }

  const passwordField = (key, label) => (
    <FormRow label={label} error={errors[key]}>
      {(id) => (
        <TextField
          id={id}
          type={reveal[key] ? 'text' : 'password'}
          value={values[key]}
          onChange={set(key)}
          invalid={!!errors[key]}
          autoComplete="new-password"
          trailing={
            <button
              type="button"
              onClick={() => setReveal((previous) => ({ ...previous, [key]: !previous[key] }))}
              aria-label={label}
            >
              {reveal[key] ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          }
        />
      )}
    </FormRow>
  )

  return (
    <Drawer
      dialog
      open={open}
      title={S.create.title}
      icon={UserPlus}
      hint={S.create.hint}
      onClose={onClose}
      footer={
        <>
          <Button type="submit" form="create-user-form">
            {S.create.submit}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {S.close}
          </Button>
        </>
      }
    >
      <form id="create-user-form" onSubmit={submit} noValidate>
        <FormBody>
          {/* The fetch sits ON the username row because the username is what it takes —
              a button anywhere else would be a second thing to find. */}
          <FormRow
            label={S.create.username}
            error={errors.username}
            hint={sources.length > 0 ? S.fetch.hint : undefined}
          >
            {(id) => (
              <div className="user-create__username">
                <TextField
                  id={id}
                  value={values.username}
                  onChange={set('username')}
                  invalid={!!errors.username}
                  autoComplete="off"
                />
                {sources.length > 0 && (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={fetching}
                    onClick={fetchFromSource}
                  >
                    <Download size={16} strokeWidth={1.75} />
                    {fetching ? S.fetch.running : S.fetch.action}
                  </Button>
                )}
              </div>
            )}
          </FormRow>

          {/* What was fetched, named. The account is created with these even though the
              form has no field for them, so the reader has to be able to see them. */}
          {fetched && (
            <InfoNote lead={S.fetch.filled}>
              <bdi>
                {[fetched.nameAr || fetched.nameEn, fetched.groupLabel]
                  .filter(Boolean)
                  .join(' — ')}
              </bdi>
            </InfoNote>
          )}

          <FormRow label={S.create.email} error={errors.email}>
            {(id) => (
              <TextField
                id={id}
                type="email"
                dir="ltr"
                value={values.email}
                onChange={set('email')}
                invalid={!!errors.email}
                autoComplete="off"
              />
            )}
          </FormRow>

          {/* The password and its confirmation are ONE decision, so they sit on
              one line where the two can be compared without scrolling. */}
          <FormCols>
            {passwordField('password', S.create.password)}
            {passwordField('confirmPassword', S.create.confirmPassword)}
          </FormCols>
        </FormBody>
      </form>
    </Drawer>
  )
}
